import React, { useState, useEffect, useMemo } from 'react';
import { Alert } from 'react-bootstrap';
import { TrendingDown, TrendingUp, ArrowLeftRight, CheckCircle, AlertTriangle } from 'lucide-react';
import {
    buildRecipe,
    previewVerification,
    toUserErrorMessage,
} from '../../../services/BookkeepingTemplateService';
import { applyFilter } from '../BookkeepingTemplates/BasAccountPicker';
import { resolveRoleAccount } from '../../../services/api/chartRolesApi';
import TplSelect from '../Common/TplSelect';
import FieldLabel from '../Common/FieldLabel';
import './GuidedTemplateForm.css';

/* ==================================================================
 * GuidedTemplateForm — the simple, no-formulas way to create a
 * template. It only collects plain choices and hands them to the
 * backend recipe builder (POST /recipe); it carries zero accounting
 * logic itself. The built definition is lifted to the parent via
 * `onChange` for saving / handing off to the advanced editor.
 * ================================================================== */

const RECIPE_TYPES = [
    { value: 'cost', label: 'Kostnad', desc: 'Något företaget köper eller betalar för', Icon: TrendingDown },
    { value: 'income', label: 'Intäkt', desc: 'Något företaget säljer eller får betalt för', Icon: TrendingUp },
    { value: 'transfer', label: 'Överföring', desc: 'Pengar flyttas mellan företagets egna konton', Icon: ArrowLeftRight },
];

const VAT_RATES = [
    { value: 0, label: 'Ingen moms' },
    { value: 6, label: '6 %' },
    { value: 12, label: '12 %' },
    { value: 25, label: '25 %' },
];

const VAT_TYPES = [
    { value: 'goods', label: 'Varor' },
    { value: 'service', label: 'Tjänst' },
];

const VAT_AREAS = [
    { value: 'domestic', label: 'Inrikes' },
    { value: 'eu', label: 'EU' },
    { value: 'non_eu', label: 'Utanför EU' },
];

function formatAmount(n) {
    if (typeof n !== 'number' || !Number.isFinite(n)) return '';
    return n.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function GuidedTemplateForm({ companyId, accounts, onChange }) {
    const [recipeType, setRecipeType] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [vatArea, setVatArea] = useState('domestic');
    const [vatRate, setVatRate] = useState(25);
    const [vatType, setVatType] = useState('goods');
    const [account, setAccount] = useState('');
    // Prefilled from the tenant's primary_bank chart role — never a pinned
    // number; the user can pick any class-1 account.
    const [paymentAccount, setPaymentAccount] = useState('');
    const [fromAccount, setFromAccount] = useState('');
    const [toAccount, setToAccount] = useState('');

    useEffect(() => {
        if (!companyId) return undefined;
        let active = true;
        resolveRoleAccount(companyId, 'primary_bank').then((leg) => {
            if (active && leg) setPaymentAccount((current) => current || leg.accountNumber);
        });
        return () => { active = false; };
    }, [companyId]);

    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    // The choices object — null until enough is filled in to build.
    const choices = useMemo(() => {
        if (!recipeType || !title.trim()) return null;
        if (recipeType === 'transfer') {
            if (!fromAccount || !toAccount) return null;
            return {
                recipe_type: 'transfer',
                title: title.trim(),
                description: description.trim() || undefined,
                from_account: fromAccount,
                to_account: toAccount,
            };
        }
        if (!account || !paymentAccount) return null;
        return {
            recipe_type: recipeType,
            title: title.trim(),
            description: description.trim() || undefined,
            vat_area: vatArea,
            vat_rate: vatRate,
            vat_type: vatType,
            account,
            payment_account: paymentAccount,
        };
    }, [recipeType, title, description, vatArea, vatRate, vatType, account, paymentAccount, fromAccount, toAccount]);

    const choicesKey = useMemo(() => JSON.stringify(choices), [choices]);

    // Debounced: choices → /recipe (build the template) → /preview
    // (evaluate it) so the user sees the resulting verifikat live.
    useEffect(() => {
        if (!choices) {
            setPreview(null);
            setError(null);
            onChange(null);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const definition = await buildRecipe(companyId, choices);
                if (cancelled) return;
                setError(null);
                onChange(definition);
                const inputs = {};
                for (const i of definition.body.inputs) inputs[i.name] = i.example;
                const result = await previewVerification(companyId, definition.body, inputs, {
                    applicableCompanyTypes: definition.applicable_company_types,
                    supportedChartVersions: definition.supported_chart_versions,
                    supportedAccountingRegimes: definition.supported_accounting_regimes,
                });
                if (!cancelled) setPreview(result);
            } catch (err) {
                if (!cancelled) {
                    setError(toUserErrorMessage(err));
                    setPreview(null);
                    onChange(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 400);
        return () => { cancelled = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [choicesKey]);

    const accountLabel = recipeType === 'income' ? 'Intäktskonto' : 'Kostnadskonto';
    const accountHint = recipeType === 'income'
        ? 'Kontot som intäkten bokförs på — ett intäktskonto i klass 3.'
        : 'Kontot som kostnaden bokförs på — ett kostnadskonto i klass 4–7 '
            + '(4 = varor/material, 5–6 = övriga kostnader, 7 = personalkostnader).';

    // Account options for the themed TplSelect dropdowns — same shape
    // and look as the advanced editor. Pre-filtered per BAS class so
    // the guided user is offered the relevant accounts.
    const accountOptions = useMemo(() => {
        const toOpts = (list) => list.map((a) => ({
            value: a.accountNumber,
            label: `${a.accountNumber} — ${a.accountName}`,
        }));
        return {
            all: toOpts(accounts),
            cost: toOpts(applyFilter(accounts, 'class:4-7')),
            income: toOpts(applyFilter(accounts, 'class:3')),
            payment: toOpts(applyFilter(accounts, 'class:1')),
        };
    }, [accounts]);
    const mainAccountOptions = recipeType === 'income' ? accountOptions.income : accountOptions.cost;

    const lines = preview && Array.isArray(preview.lines) ? preview.lines : [];
    const totals = (preview && preview.totals) || { debit: 0, credit: 0 };
    const balanced = !!(preview && preview.ok);

    return (
        <div className="gtf">
            {/* Step 1 — what kind of template */}
            <section className="tpl-section">
                <header className="tpl-section-head">
                    <div>
                        <h3>Vad ska mallen bokföra?</h3>
                        <p>Välj typ av affärshändelse — vi föreslår konton åt dig.</p>
                    </div>
                </header>
                <div className="tpl-section-body">
                    <div className="gtf-types">
                        {RECIPE_TYPES.map(({ value, label, desc, Icon }) => (
                            <button type="button" key={value}
                                className={`gtf-type ${recipeType === value ? 'active' : ''}`}
                                onClick={() => { setRecipeType(value); setVatArea('domestic'); }}>
                                <Icon size={22} />
                                <span className="gtf-type-label">{label}</span>
                                <span className="gtf-type-desc">{desc}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Step 2 — details (revealed once a type is chosen) */}
            {recipeType && (
                <section className="tpl-section">
                    <header className="tpl-section-head">
                        <div>
                            <h3>Detaljer</h3>
                            <p>Namnge mallen och välj kontona.</p>
                        </div>
                    </header>
                    <div className="tpl-section-body">
                        <div className="tpl-field">
                            <FieldLabel text="Mallnamn"
                                hint="Namnet du och dina kollegor ser när ni väljer mall vid bokföring." />
                            <input type="text" className="tpl-input" value={title}
                                placeholder={recipeType === 'income' ? 't.ex. Försäljning av varor'
                                    : recipeType === 'transfer' ? 't.ex. Överföring till sparkonto'
                                        : 't.ex. Inköp av kontorsmaterial'}
                                onChange={(e) => setTitle(e.target.value)} />
                        </div>
                        <div className="tpl-field">
                            <label className="tpl-label">Beskrivning (valfritt)</label>
                            <textarea className="tpl-input tpl-textarea" rows={2} value={description}
                                placeholder="Kort beskrivning av när mallen ska användas."
                                onChange={(e) => setDescription(e.target.value)} />
                        </div>

                        {recipeType === 'cost' && (
                            <>
                                <div className="tpl-grid-3">
                                    <div className="tpl-field">
                                        <FieldLabel text="Var köper du ifrån?"
                                            hint="Inrikes, från ett EU-land, eller utanför EU. EU och utanför EU bokförs med omvänd skattskyldighet." />
                                        <div className="tpl-chips">
                                            {VAT_AREAS.map((a) => (
                                                <button type="button" key={a.value}
                                                    className={`tpl-chip ${vatArea === a.value ? 'active' : ''}`}
                                                    onClick={() => setVatArea(a.value)}>
                                                    {a.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="tpl-field">
                                        <FieldLabel text="Moms"
                                            hint={vatArea === 'domestic'
                                                ? 'Momssatsen på affärshändelsen. 25 % är vanligast; 12 % och 6 % gäller vissa varor och tjänster.'
                                                : 'Den momssats varan/tjänsten skulle haft i Sverige — t.ex. 6 % för böcker, 12 % för livsmedel, 25 % för det mesta. Den beräknade momsen rapporteras både som ingående och utgående och nettar till noll.'} />
                                        <div className="tpl-chips">
                                            {VAT_RATES.map((r) => (
                                                <button type="button" key={r.value}
                                                    className={`tpl-chip ${vatRate === r.value ? 'active' : ''}`}
                                                    onClick={() => setVatRate(r.value)}>
                                                    {r.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="tpl-field">
                                        <FieldLabel text="Varor eller tjänst?"
                                            hint="Om inköpet avser fysiska varor eller en tjänst. Påverkar konto och momsrapportering." />
                                        <div className="tpl-chips">
                                            {VAT_TYPES.map((t) => (
                                                <button type="button" key={t.value}
                                                    className={`tpl-chip ${vatType === t.value ? 'active' : ''}`}
                                                    onClick={() => setVatType(t.value)}>
                                                    {t.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                {vatArea !== 'domestic' && (
                                    <p className="gtf-rc-note">
                                        Inköp från {vatArea === 'eu' ? 'EU' : 'länder utanför EU'} bokförs
                                        med <strong>omvänd skattskyldighet</strong> — säljaren fakturerar
                                        utan moms, och {vatRate > 0
                                            ? <>den beräknade momsen ({vatRate}&nbsp;%) rapporteras både som ingående och utgående (nettar till noll).</>
                                            : <>ingen moms beräknas (0&nbsp;% — t.ex. skattefri tjänst).</>}
                                    </p>
                                )}
                                <div className="tpl-grid-2">
                                    <div className="tpl-field">
                                        <FieldLabel text={accountLabel} hint={accountHint} />
                                        <TplSelect options={mainAccountOptions} value={account}
                                            onChange={setAccount} placeholder="Sök konto…" />
                                    </div>
                                    <div className="tpl-field">
                                        <FieldLabel text="Betalkonto"
                                            hint="Kontot pengarna betalas från eller till — oftast företagskontot." />
                                        <TplSelect options={accountOptions.payment} value={paymentAccount}
                                            onChange={setPaymentAccount} placeholder="Sök betalkonto…" />
                                    </div>
                                </div>
                            </>
                        )}

                        {recipeType === 'income' && (
                            <>
                                <div className="tpl-grid-2">
                                    <div className="tpl-field">
                                        <FieldLabel text="Vart säljer du?"
                                            hint="Inrikes, till ett företag i ett annat EU-land, eller till en kund utanför EU. EU-företagsförsäljning och export sker utan svensk moms." />
                                        <div className="tpl-chips">
                                            {VAT_AREAS.map((a) => (
                                                <button type="button" key={a.value}
                                                    className={`tpl-chip ${vatArea === a.value ? 'active' : ''}`}
                                                    onClick={() => setVatArea(a.value)}>
                                                    {a.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    {vatArea === 'domestic' ? (
                                        <div className="tpl-field">
                                            <FieldLabel text="Moms"
                                                hint="Momssatsen på försäljningen. 25 % är vanligast; 12 % och 6 % gäller vissa varor och tjänster." />
                                            <div className="tpl-chips">
                                                {VAT_RATES.map((r) => (
                                                    <button type="button" key={r.value}
                                                        className={`tpl-chip ${vatRate === r.value ? 'active' : ''}`}
                                                        onClick={() => setVatRate(r.value)}>
                                                        {r.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="tpl-field">
                                            <FieldLabel text="Varor eller tjänst?"
                                                hint="Om försäljningen avser varor eller en tjänst. Påverkar konto och momsrapportering." />
                                            <div className="tpl-chips">
                                                {VAT_TYPES.map((t) => (
                                                    <button type="button" key={t.value}
                                                        className={`tpl-chip ${vatType === t.value ? 'active' : ''}`}
                                                        onClick={() => setVatType(t.value)}>
                                                        {t.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {vatArea === 'eu' && (
                                    <p className="gtf-rc-note">
                                        Försäljning till företag i EU bokförs <strong>utan svensk moms</strong> —
                                        köparen redovisar momsen i sitt land. Kundens VAT-nummer anges
                                        när mallen används.
                                    </p>
                                )}
                                {vatArea === 'non_eu' && (
                                    <p className="gtf-rc-note">
                                        Försäljning till kund utanför EU (export) bokförs <strong>utan
                                        svensk moms</strong>. För varuexport behöver du kunna styrka att
                                        varan lämnat EU.
                                    </p>
                                )}
                                <div className="tpl-grid-2">
                                    <div className="tpl-field">
                                        <FieldLabel text={accountLabel} hint={accountHint} />
                                        <TplSelect options={mainAccountOptions} value={account}
                                            onChange={setAccount} placeholder="Sök konto…" />
                                    </div>
                                    <div className="tpl-field">
                                        <FieldLabel text="Betalkonto"
                                            hint="Kontot pengarna betalas från eller till — oftast företagskontot." />
                                        <TplSelect options={accountOptions.payment} value={paymentAccount}
                                            onChange={setPaymentAccount} placeholder="Sök betalkonto…" />
                                    </div>
                                </div>
                            </>
                        )}

                        {recipeType === 'transfer' && (
                            <div className="tpl-grid-2">
                                <div className="tpl-field">
                                    <FieldLabel text="Från konto"
                                        hint="Kontot pengarna flyttas från — krediteras." />
                                    <TplSelect options={accountOptions.all} value={fromAccount}
                                        onChange={setFromAccount} placeholder="Sök konto…" />
                                </div>
                                <div className="tpl-field">
                                    <FieldLabel text="Till konto"
                                        hint="Kontot pengarna flyttas till — debiteras." />
                                    <TplSelect options={accountOptions.all} value={toAccount}
                                        onChange={setToAccount} placeholder="Sök konto…" />
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Step 3 — live verifikat preview */}
            {recipeType && (
                <section className="tpl-section">
                    <header className="tpl-section-head">
                        <div>
                            <h3>Så här bokförs det</h3>
                            <p>Verifikatet mallen skapar — med ett exempelbelopp.</p>
                        </div>
                    </header>
                    <div className="tpl-section-body">
                        {error && (
                            <Alert variant="danger" className="tpl-alert mb-0">
                                <AlertTriangle size={15} /> {error}
                            </Alert>
                        )}
                        {!error && !choices && (
                            <p className="gtf-empty">Fyll i mallnamn och konton ovan för att se förhandsvisningen.</p>
                        )}
                        {!error && choices && loading && !preview && (
                            <p className="gtf-empty">Beräknar…</p>
                        )}
                        {!error && choices && preview && (
                            <>
                                <div className={`gtf-status ${balanced ? 'ok' : 'bad'}`}>
                                    {balanced
                                        ? <><CheckCircle size={15} /> Balanserat verifikat</>
                                        : <><AlertTriangle size={15} /> Verifikatet stämmer inte</>}
                                </div>
                                <table className="gtf-table">
                                    <thead>
                                        <tr>
                                            <th>Konto</th>
                                            <th>Benämning</th>
                                            <th className="num">Debet</th>
                                            <th className="num">Kredit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lines.map((l, i) => (
                                            <tr key={i}>
                                                <td className="acc">{l.account}</td>
                                                <td>{l.account_name || ''}</td>
                                                <td className="num">{l.side === 'debit' ? formatAmount(l.amount) : ''}</td>
                                                <td className="num">{l.side === 'credit' ? formatAmount(l.amount) : ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={2}>Summa</td>
                                            <td className="num">{formatAmount(totals.debit)}</td>
                                            <td className="num">{formatAmount(totals.credit)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                                <p className="gtf-preview-note">
                                    Exempelbelopp. När mallen används anger användaren det verkliga beloppet.
                                </p>
                            </>
                        )}
                    </div>
                </section>
            )}
        </div>
    );
}

export default GuidedTemplateForm;
